import React, { useState, useEffect, useCallback } from 'react';
import { stocks, stations } from '../services/api';
import Pagination from './Pagination';

const MasterStocks = ({ user }) => {
  const [stockList, setStockList] = useState([]);
  const [stationList, setStationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null); // part_number being saved
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStation, setSelectedStation] = useState(
    user?.role === 'admin' ? String(user.station_id) : ''
  );
  const [editValues, setEditValues] = useState({}); // { [part_number]: stockQty }
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [pagination, setPagination] = useState({
    total: 0, last_page: 1, per_page: 50, current_page: 1, from: 0, to: 0
  });

  // Fetch stations list for superadmin dropdown
  useEffect(() => {
    if (user?.role === 'superadmin') {
      stations.getAll().then(res => {
        setStationList(res.data.data || res.data || []);
      }).catch(console.error);
    }
  }, [user]);

  const fetchStocks = useCallback(async (page = 1) => {
    if (!selectedStation) return;
    setLoading(true);
    try {
      const params = { station_id: selectedStation, search: searchTerm, page, per_page: 50 };
      const res = await stocks.getAll(params);
      const data = res.data.data || [];
      setStockList(data);
      setPagination(res.data.pagination || {
        total: data.length, last_page: 1, per_page: 50,
        current_page: 1, from: 1, to: data.length
      });
      // Seed edit values from fetched data
      const vals = {};
      data.forEach(item => { vals[item.part_number] = item.stock ?? 0; });
      setEditValues(prev => ({ ...vals, ...prev }));
    } catch (err) {
      console.error('Failed to fetch stocks', err);
    } finally {
      setLoading(false);
    }
  }, [selectedStation, searchTerm]);

  useEffect(() => { fetchStocks(1); }, [fetchStocks]);

  const handleStockChange = (partNumber, value) => {
    setEditValues(prev => ({ ...prev, [partNumber]: value === '' ? '' : parseInt(value) || 0 }));
  };

  const handleSave = async (partNumber) => {
    setSaving(partNumber);
    setSuccessMsg('');
    setErrorMsg('');
    try {
      const payload = {
        part_number: partNumber,
        stock: parseInt(editValues[partNumber]) || 0,
      };
      if (user?.role !== 'admin') {
        payload.station_id = parseInt(selectedStation);
      }
      await stocks.update(payload);
      setSuccessMsg(`Stok untuk ${partNumber} berhasil disimpan.`);
      setTimeout(() => setSuccessMsg(''), 3000);
      // Update local list
      setStockList(prev =>
        prev.map(item =>
          item.part_number === partNumber ? { ...item, stock: payload.stock } : item
        )
      );
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Gagal menyimpan stok.');
      setTimeout(() => setErrorMsg(''), 4000);
    } finally {
      setSaving(null);
    }
  };
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);
    try {
      await stocks.import(formData);
      setSuccessMsg('Import CSV berhasil.');
      // Refresh stock list after successful import
      fetchStocks(1);
      // Auto reload the page to reflect any changes
      window.location.reload();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Gagal mengimport CSV.');
    } finally {
      setLoading(false);
      // Reset file input value to allow re-importing same file if needed
      event.target.value = '';
    }
  };
  const stockBadgeClass = (qty) => {
    if (qty === 0) return 'bg-red-100 text-red-700 border-red-200';
    if (qty <= 5) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const stockLabel = (qty) => {
    if (qty === 0) return 'Habis';
    if (qty <= 5) return 'Menipis';
    return 'Tersedia';
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manajemen Stok</h2>
          <p className="text-gray-500 text-sm mt-1">
            Kelola jumlah stok spare part per AHASS.
            {user?.role === 'admin' && user?.station_name && (
              <span className="ml-2 font-semibold text-blue-600">— {user.station_name}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
            <a
              href="/stock_template.csv"
              download
              className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition ml-2"
            >
              Download Template
            </a>
          <button
            onClick={() => document.getElementById('stock-import-input').click()}
            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition"
          >
            Import CSV
          </button>
          <input
            type="file"
            accept=".csv"
            id="stock-import-input"
            style={{ display: 'none' }}
            onChange={handleImport}
          />
          <span className="text-xs text-gray-400">Total Part:</span>
          <span className="text-sm font-bold text-gray-700">{pagination.total.toLocaleString()}</span>
        </div>
      </div>

      {/* Import & Format Instructions */}
      <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-xl text-sm leading-relaxed shadow-sm">
        <h4 className="font-semibold mb-1 flex items-center gap-1.5 text-blue-900">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 shrink-0">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 111.063.852l-.708 2.836a.75.75 0 001.063.852l.041-.028M12 20.25a8.25 8.25 0 100-16.5 8.25 8.25 0 000 16.5z" />
          </svg>
          Panduan Import & Input Stok
        </h4>
        <ul className="list-disc pl-5 space-y-1 text-xs md:text-sm text-blue-700">
          <li>Format file import wajib berupa <strong>CSV</strong> dengan header: <code>part_number,stock</code>.</li>
          <li>Apabila part number dimulai dengan angka nol (contoh: <code>08123...</code>), pastikan kolom di Excel telah diformat sebagai <strong>Teks</strong> atau tambahkan tanda petik tunggal (<code>'</code>) di awal nomor part (contoh: <code>'08123...</code>) agar angka nol tidak terhapus.</li>
          <li>Sistem secara otomatis mendeteksi dan menghapus tanda petik tunggal (<code>'</code>) saat pencarian maupun import stok dilakukan.</li>
        </ul>
      </div>

      {/* Success / Error banner */}
      {successMsg && (
        <div className="bg-green-50 text-green-700 border border-green-200 rounded-xl px-5 py-3 text-sm font-medium flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl px-5 py-3 text-sm font-medium flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {errorMsg}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center">
        <div className="w-full md:w-80 relative">
          <input
            type="text"
            placeholder="Cari part number atau nama part..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
          />
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {user?.role === 'superadmin' && (
          <select
            value={selectedStation}
            onChange={(e) => setSelectedStation(e.target.value)}
            className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-white"
          >
            <option value="">— Pilih AHASS —</option>
            {stationList.map((st) => (
              <option key={st.id} value={st.id}>{st.name}</option>
            ))}
          </select>
        )}

        {!selectedStation && user?.role === 'superadmin' && (
          <p className="text-sm text-yellow-600 font-medium">Pilih AHASS terlebih dahulu untuk melihat stok.</p>
        )}
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Part Number</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nama Part</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Harga</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-48">Jumlah Stok</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
                      <span className="text-sm font-medium">Memuat data stok...</span>
                    </div>
                  </td>
                </tr>
              ) : !selectedStation ? (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-300">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                      <p className="text-sm font-medium text-gray-400">Pilih AHASS untuk melihat data stok</p>
                    </div>
                  </td>
                </tr>
              ) : stockList.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500 font-medium">
                    Tidak ada data spare part ditemukan.
                  </td>
                </tr>
              ) : (
                stockList.map((item) => {
                  const currentStock = editValues[item.part_number] ?? item.stock ?? 0;
                  const isSaving = saving === item.part_number;
                  const isDiscontinued = item.is_discontinued || item.price <= 0 || item.price === null;
                  return (
                    <tr key={item.part_number} className={`transition duration-150 ${isDiscontinued ? 'bg-gray-50/60 opacity-80' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-xs font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                          {item.part_number}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                          {item.part_name}
                          {isDiscontinued && (
                            <span className="bg-gray-200 text-gray-700 text-[10px] uppercase font-bold px-2 py-0.5 rounded">
                              Diskontinyu
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{item.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-blue-600">
                          {isDiscontinued ? '-' : `Rp ${(item.price || 0).toLocaleString('id-ID')}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${isDiscontinued ? 'bg-gray-100 text-gray-600 border-gray-300' : stockBadgeClass(item.stock ?? 0)}`}>
                          {isDiscontinued ? 'Diskontinyu' : stockLabel(item.stock ?? 0)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            disabled={isDiscontinued}
                            onClick={() => handleStockChange(item.part_number, Math.max(0, (currentStock || 0) - 1))}
                            className={`w-7 h-7 rounded-lg font-bold text-base flex items-center justify-center transition ${
                              isDiscontinued ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                            }`}
                          >−</button>
                          <input
                            type="number"
                            min="0"
                            disabled={isDiscontinued}
                            value={currentStock}
                            onChange={(e) => handleStockChange(item.part_number, e.target.value)}
                            className={`w-16 text-center border border-gray-300 rounded-lg py-1 text-sm font-bold outline-none ${
                              isDiscontinued ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'focus:ring-2 focus:ring-blue-500'
                            }`}
                          />
                          <button
                            type="button"
                            disabled={isDiscontinued}
                            onClick={() => handleStockChange(item.part_number, (currentStock || 0) + 1)}
                            className={`w-7 h-7 rounded-lg font-bold text-base flex items-center justify-center transition ${
                              isDiscontinued ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                            }`}
                          >+</button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleSave(item.part_number)}
                          disabled={isSaving || isDiscontinued}
                          className={`px-4 py-1.5 rounded-lg text-xs font-bold transition shadow-sm ${
                            isDiscontinued
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                              : isSaving
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200'
                          }`}
                        >
                          {isSaving ? (
                            <span className="flex items-center gap-1.5">
                              <svg className="w-3 h-3 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                              </svg>
                              Simpan...
                            </span>
                          ) : 'Simpan'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && stockList.length > 0 && (
          <Pagination
            pagination={pagination}
            onPageChange={(page) => fetchStocks(page)}
          />
        )}
      </div>
    </div>
  );
};

export default MasterStocks;
